import * as React from 'react';
import { MassageTypeSelect, type MassageTypeOption } from '../MassageUsages/MassageTypeSelect';

export interface VoucherReconcileFormProps {
    massageTypes: MassageTypeOption[];
    massageType: string;
    onMassageTypeChange: (type: string) => void;
    onSelectMassageType: (option: MassageTypeOption) => void;
    price: number;
    onPriceChange: (price: number) => void;
    receiptDate: string;
    onReceiptDateChange: (date: string) => void;
    submitting: boolean;
    error: string | null;
    onBack: () => void;
    onSubmit: () => void;
}

/**
 * Mechanically extracted from VoucherActionModal to keep that file under
 * the 500 LOC cap. No logic changes beyond what already lived here.
 *
 * The Service Type field relies on MassageTypeSelect's Enter contract: it
 * forwards Enter to onKeyDown only when it did not itself consume the key
 * (i.e. no highlighted dropdown option to select). That is what makes both
 * "select an option, then Enter again to submit" and "free-text a brand
 * new service name and hit Enter to submit" work without double-firing.
 */
export const VoucherReconcileForm: React.FC<VoucherReconcileFormProps> = ({
    massageTypes,
    massageType,
    onMassageTypeChange,
    onSelectMassageType,
    price,
    onPriceChange,
    receiptDate,
    onReceiptDateChange,
    submitting,
    error,
    onBack,
    onSubmit,
}) => {
    return (
        <div className="convert-form">
            <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Service Type</label>
                <MassageTypeSelect
                    options={massageTypes}
                    value={massageType}
                    onChange={onMassageTypeChange}
                    onSelect={onSelectMassageType}
                    disabled={submitting}
                    autoFocus
                    placeholder="e.g. Thai Massage, Oil Massage..."
                    className="form-control form-control-sm"
                    onKeyDown={e => { if (e.key === 'Enter' && massageType.trim()) onSubmit(); }}
                />
            </div>
            <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Price (THB)</label>
                <input
                    type="number"
                    className="form-control form-control-sm"
                    value={price}
                    onChange={e => onPriceChange(parseFloat(e.target.value) || 0)}
                    min={0}
                    step={50}
                    disabled={submitting}
                    onKeyDown={e => { if (e.key === 'Enter') onSubmit(); }}
                />
            </div>
            <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Receipt Date</label>
                <input
                    type="date"
                    className="form-control form-control-sm"
                    value={receiptDate}
                    onChange={e => onReceiptDateChange(e.target.value)}
                    disabled={submitting}
                />
            </div>
            {error && (
                <div style={{ color: '#991b1b', fontSize: '13px', marginBottom: '8px' }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
                <button
                    onClick={onBack}
                    disabled={submitting}
                    style={{
                        flex: '0 0 auto', padding: '10px 16px', borderRadius: '6px',
                        border: '1px solid #d1d5db', background: '#fff', color: '#374151',
                        fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                    }}
                >
                    Back
                </button>
                <button
                    onClick={onSubmit}
                    disabled={submitting || !massageType.trim()}
                    style={{
                        flex: 1, padding: '10px', borderRadius: '6px', border: 'none',
                        fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                        background: '#059669', color: '#fff',
                    }}
                >
                    {submitting ? 'Saving...' : 'Reconcile'}
                </button>
            </div>
        </div>
    );
};
